/**
 * Frontend idle session: logout after 8 hours with no user or API activity.
 */

export const SESSION_IDLE_MS = 8 * 60 * 60 * 1000
/** How often to check whether the idle window has elapsed */
export const SESSION_IDLE_CHECK_MS = 15_000

const STORAGE_KEY = 'cstore_last_api_activity'

let lastActivityMs = 0
let idleLogoutInProgress = false

function readStored(): number {
  if (globalThis.window === undefined) return 0
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return 0
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

function writeStored(ms: number): void {
  if (globalThis.window === undefined) return
  try {
    sessionStorage.setItem(STORAGE_KEY, String(ms))
  } catch {
    // ignore quota / private mode
  }
}

/** Record API / login activity so the idle clock restarts. */
export function touchSessionActivity(): void {
  const now = Date.now()
  lastActivityMs = now
  writeStored(now)
}

export function getLastSessionActivity(): number {
  if (lastActivityMs > 0) return lastActivityMs
  const stored = readStored()
  if (stored > 0) {
    lastActivityMs = stored
    return stored
  }
  return 0
}

export function clearSessionActivity(): void {
  lastActivityMs = 0
  if (globalThis.window === undefined) return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function isIdleLogoutInProgress(): boolean {
  return idleLogoutInProgress
}

/**
 * Call Nest logout, clear FE auth, send user to login.
 * Safe to call once; concurrent calls are ignored.
 * Also broadcasts so other open tabs refresh to login.
 */
export async function performIdleLogout(): Promise<void> {
  if (idleLogoutInProgress) return
  if (globalThis.window === undefined) return
  idleLogoutInProgress = true

  try {
    const { authRepository, clearApiToken } = await import('@/libs/api')
    const { broadcastLogout } = await import('@/libs/auth-cross-tab')
    try {
      await authRepository.logout()
    } catch {
      // Cookie may already be invalid — still clear FE state
    }
    clearApiToken()
    clearSessionActivity()
    try {
      sessionStorage.removeItem('app_initialized')
    } catch {
      // ignore
    }

    const { signOut } = await import('next-auth/react')
    await signOut({ redirect: false })
    broadcastLogout()
  } finally {
    const pathname = globalThis.location.pathname
    const locale = pathname.startsWith('/ar') ? 'ar' : 'en'
    globalThis.location.href = `/${locale}/login`
  }
}
